import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from '@/portainer/services/notifications';
import { pluralize } from '@/portainer/helpers/strings';

import { buildUrl } from '../environment.service/utils';
import { EnvironmentId } from '../types';

interface EnvironmentToDelete {
  id: EnvironmentId;
  name: string;
  deleteCluster?: boolean;
}

const deleteEnvironmentsMutationKey = ['environments', 'delete'];

export function useDeleteEnvironmentsMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    async (environments: EnvironmentToDelete[]) => {
      const resp = await deleteEnvironments(environments);

      // a 204 has an empty body, which axios gives as ''
      if (!resp) {
        return {
          deleted: environments,
          errors: [],
          clusterErrors: [],
          removing: [],
        };
      }

      return {
        deleted: environments.filter((e) =>
          (resp.deleted || []).includes(e.id)
        ),
        errors: environments.filter((e) => (resp.errors || []).includes(e.id)),
        clusterErrors: (resp.clusterErrors || []).flatMap(({ id, error }) => {
          const environment = environments.find((e) => e.id === id);
          return environment ? [{ environment, error }] : [];
        }),
        removing: environments.filter((e) =>
          (resp.removing || []).includes(e.id)
        ),
      };
    },
    {
      mutationKey: deleteEnvironmentsMutationKey,
      ...withError('Unable to delete environment(s)'),
      onSuccess: ({ deleted, errors, clusterErrors, removing }) => {
        queryClient.invalidateQueries(['environments']);
        // show an error message for each env that failed to delete
        errors.forEach((e) => {
          notifyError(`Failed to remove environment ${e.name}`, undefined);
        });
        // warn for each removed env whose cluster couldn't be deleted
        clusterErrors.forEach(({ environment, error }) => {
          notifyWarning(
            `${environment.name} removed, but its cluster may still exist`,
            error
          );
        });
        // show one summary message for all successful deletes
        if (deleted.length) {
          notifySuccess(
            `${pluralize(deleted.length, 'Environment')} successfully removed`,
            deleted.map((d) => d.name).join(', ')
          );
        }
        // KubeSolo environments are removed in the background, and show their progress in the list
        if (removing.length) {
          notifySuccess(
            `Uninstalling KubeSolo from ${removing.length} ${pluralize(removing.length, 'machine')}`,
            `${removing.map((e) => e.name).join(', ')}. ${pluralize(removing.length, 'The environment is', 'Each environment is')} removed when its uninstall finishes.`
          );
        }
      },
    }
  );
}

/** Whether a removal request is running, since deleting a cluster can take a while */
export function useIsRemovingEnvironments() {
  return useIsMutating({ mutationKey: deleteEnvironmentsMutationKey }) > 0;
}

async function deleteEnvironments(environments: EnvironmentToDelete[]) {
  try {
    const { data } = await axios.post<{
      deleted: EnvironmentId[];
      errors: EnvironmentId[];
      clusterErrors?: { id: EnvironmentId; error: string }[];
      removing?: EnvironmentId[];
    } | null>(buildUrl(undefined, 'delete'), {
      endpoints: environments.map(({ id, name, deleteCluster }) => ({
        id,
        name,
        deleteCluster,
      })),
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to delete environment');
  }
}
